import { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import * as PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useElements, CardCvcElement, useStripe } from '@stripe/react-stripe-js'
import { fromZonedTime, toZonedTime, format } from 'date-fns-tz'

import AmenityItem from '../AmenityItem/AmenityItem'
import { AmenitySearch, filterAmenitiesByName } from '../AmenitySearch'
import { selectStatusIsLoadedAll, getClients } from '../../../reducers/clientsReducer'
import { selectUserData } from '../../../reducers/userDataReducer'
import {
  selectAmenities,
  getAmenities,
  toggleDashboard,
} from '../../../reducers/Organisation/reservationsReducer'
import ReservationFormFooter from '../ReservationFormFooter/ReservationFormFooter'
import Stepper from '../../../components/Stepper/Stepper'
import AmenityReservationCalendar from '../../ClientReservations/Amenities/AmenityReservationCalendar/AmenityReservationCalendar'
import { selectBuildingGuests } from '../../../reducers/guestsReducer'
import api from '../../../api/api'
import { getFinalPrice } from '../../ClientReservations/Amenities/helpers'
import Alert from '../../../Alert'
import ModalContainer from '../../ModalContainer/ModelContainer'
import Icon from '../../Icons'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { TIMEZONES } from '../../../helpers/constants'
import { getMinutesofDayFromDate } from '../../../helpers/helpers'

import ReservationCalendarSummary from './ReservationCalendarSummary'
import ReservationTimeSelect from './ReservationTimeSelect'
import ReservationSummary from './ReservationSummary'
import ReservationOccupantInfo, { OccupantType } from './ReservationOccupantInfo'
import { ModifiedPriceType } from './types'
import {
  calculateFinalPrice,
  calculateDaysBetweenDates,
  filterAvailablePlacesByCapacity,
  getFilteredTimeSlotsForLocation,
} from './helper'
import AddReservationGuestForm from './AddReservationGuestForm'
import CreditCardInfoForm from './CreditCardInfoForm'
import BookingDurationLimits from './BookingDurationLimits'
const getViewHeaders = (t) => ({
  SelectAmenity: t('reservations:reservationForClientForm.viewHeaders.selectAmenity'),
  SelectLocation: t('reservations:reservationForClientForm.viewHeaders.selectLocation'),
  SelectDates: t('reservations:reservationForClientForm.viewHeaders.selectDates'),
  SelectDate: t('reservations:reservationForClientForm.viewHeaders.selectDate'),
  SelectTime: t('reservations:reservationForClientForm.viewHeaders.selectTime'),
  OccupantInfo: t('reservations:reservationForClientForm.viewHeaders.occupantInfo'),
  AddNewGuest: t('reservations:addReservationGuestForm.addNewGuest'),
  UpdateCreditCard: t('reservations:reservationForClientForm.viewHeaders.updateCreditCard'),
  AddCreditCard: t('reservations:reservationForClientForm.viewHeaders.addCreditCard'),
})

export const Views = {
  SelectAmenity: 'SelectAmenity',
  SelectLocation: 'SelectLocation',
  SelectDates: 'SelectDates',
  SelectDate: 'SelectDate',
  SelectTime: 'SelectTime',
  OccupantInfo: 'OccupantInfo',
  AddNewGuest: 'AddNewGuest',
  UpdateCreditCard: 'UpdateCreditCard',
  AddCreditCard: 'AddCreditCard',
}

const ReservationForClientForm = ({ handleCancel, residents, buildingData }) => {
  const { t } = useTranslation(['reservations', 'common'])
  const viewHeaders = getViewHeaders(t)
  const { displaySnackbar } = useSnackbar()
  const [amenity, setAmenity] = useState()
  const [amenitySearch, setAmenitySearch] = useState('')
  const [location, setLocation] = useState()
  const [calendarValues, setCalendarValues] = useState()
  const [selectedTimeRange, setSelectedTimeRange] = useState()
  const [activeView, setActiveView] = useState(Views.SelectAmenity)
  const [occupantType, setOccupantType] = useState(OccupantType.Resident)
  const [occupantInfo, setOccupantInfo] = useState()
  const [advancedSettings, setAdvancedSettings] = useState({
    note: false,
    noteTitle: '',
    noteBody: '',
    modifyPrice: false,
    modifyPriceType: ModifiedPriceType.CustomPrice,
    modifyPriceValue: '',
    sendReceipt: false,
  })
  const [creditCardData, setCreditCardData] = useState()
  const [creditCardToken, setCreditCardToken] = useState(null)
  const [residentCreditCardData, setResidentCreditCardData] = useState(null)
  const [isCreationInProgress, setIsCreationInProgress] = useState(false)
  const [availableLocationIds, setAvailableLocationIds] = useState([])
  const userData = useSelector(selectUserData)
  const amenities = useSelector(selectAmenities)
  const guests = useSelector(selectBuildingGuests)
  const loadedAllClients = useSelector(selectStatusIsLoadedAll)
  const [timeSlots, setTimeSlots] = useState([])
  const [loadingTimeScopes, setLoadingTimeScopes] = useState(true)
  const [isLoadingAmenities, setIsLoadingAmenities] = useState(true)
  const dispatch = useDispatch()

  const getClientsAction = (...args) => dispatch(getClients(...args))
  const getAmenitiesAction = (...args) => dispatch(getAmenities(...args))
  const toggleDashboardAction = (...args) => dispatch(toggleDashboard(...args))

  const elements = useElements()
  const stripe = useStripe()

  useEffect(() => {
    if (occupantInfo && occupantInfo.creditCardId && occupantInfo.type === OccupantType.Resident) {
      api
        .getClientCreditCardData(occupantInfo.clientId)
        .then(setResidentCreditCardData)
        .catch((error) => {
          displaySnackbar(error.message)
        })
    } else if (occupantInfo && !occupantInfo.creditCardId) {
      setResidentCreditCardData(null)
    }
  }, [occupantInfo])

  useEffect(() => {
    setLocation(undefined)
  }, [amenity])

  const availablePlaces = (amenity?.locations || []).filter((location) =>
    availableLocationIds.includes(location.id),
  )
  const hasAvailablePlaces = availablePlaces.length > 0
  const hasLocations = (amenity?.locations || []).length > 0

  const getReservationStep = () => {
    if (activeView === Views.SelectAmenity) {
      return 0
    }
    if (activeView === Views.SelectDates || activeView === Views.SelectDate) {
      return 1
    }
    if (activeView === Views.SelectLocation) {
      return 2
    }
    if (activeView === Views.SelectTime) {
      if (hasAvailablePlaces) {
        return 3
      }
      return 2
    }
    if (amenity?.multiDayBooking) {
      if (hasLocations) {
        return 3
      }
      return 2
    }
    if (hasAvailablePlaces) {
      return 4
    }
    return 3
  }

  const reservationStep = getReservationStep()

  const stepperSteps = useMemo(() => {
    const selectAmenity = t('reservations:reservationForClientForm.stepperSteps.selectAmenity')
    const selectDates = t('reservations:reservationForClientForm.stepperSteps.selectDates')
    const selectDate = t('reservations:reservationForClientForm.stepperSteps.selectDate')
    const selectPlace = t('reservations:reservationForClientForm.stepperSteps.selectPlace')
    const selectTime = t('reservations:reservationForClientForm.stepperSteps.selectTime')
    const occupant = t('reservations:reservationForClientForm.stepperSteps.occupant')

    if (amenity?.multiDayBooking) {
      if (hasLocations) {
        return [selectAmenity, selectDates, selectPlace, occupant]
      }
      return [selectAmenity, selectDates, occupant]
    }
    if (hasAvailablePlaces) {
      return [selectAmenity, selectDate, selectPlace, selectTime, occupant]
    }
    return [selectAmenity, selectDate, selectTime, occupant]
  }, [amenity?.multiDayBooking, hasAvailablePlaces, hasLocations, t])

  const timezone = useMemo(() => {
    if (buildingData) {
      if (Array.isArray(buildingData)) {
        return buildingData[0].timezone
      }
      return buildingData.timezone
    }
    if (userData.buildingId) {
      return userData.buildingId.timezone
    }
    return TIMEZONES.CT
  }, [buildingData, userData])

  const getTotalPrice = (ignoreDiscounts = false) => {
    let settingsToApply = advancedSettings
    if (ignoreDiscounts) {
      settingsToApply = {}
    }
    return calculateFinalPrice(
      amenity,
      calendarValues,
      selectedTimeRange,
      timezone,
      settingsToApply,
    )
  }

  const isFree = () => {
    return !!amenity && getTotalPrice() === 0
  }

  const fetchAmenities = async () => {
    try {
      await getAmenitiesAction(buildingData.id)
    } catch ({ message }) {
      console.error(message)
    } finally {
      setIsLoadingAmenities(false)
    }
  }

  const fetchTimeSlots = async () => {
    try {
      const responseData = await api.getTimeScopes(
        amenity.id,
        fromZonedTime(calendarValues[0].setHours(0, 0, 0, 0), timezone).getTime(),
      )

      let filteredTimeSlots = responseData

      // Filter out past time slots if it's today
      if (
        format(toZonedTime(new Date(), timezone), 'yyyy-MM-dd') ===
        format(calendarValues[0], 'yyyy-MM-dd')
      ) {
        const actualTime = getMinutesofDayFromDate(toZonedTime(new Date(), timezone))
        filteredTimeSlots = responseData.filter((data) => data.startTime > actualTime)
      }

      setTimeSlots(filteredTimeSlots)

      // Filter available places based on capacity across all time slots
      const availableLocationIds = filterAvailablePlacesByCapacity(filteredTimeSlots, amenity)
      setAvailableLocationIds(availableLocationIds)
    } catch (err) {
      console.error(err)
      setTimeSlots([])
      setAvailableLocationIds([])
    } finally {
      setLoadingTimeScopes(false)
    }
  }

  useEffect(() => {
    fetchAmenities()
  }, [])

  const isSubmitButtonDisabled = () => {
    switch (activeView) {
      case Views.SelectAmenity:
        return !amenity
      case Views.SelectLocation:
        return !location
      case Views.SelectDate:
        return !calendarValues || !calendarValues[0]
      case Views.SelectDates:
        return (
          !calendarValues ||
          !calendarValues[0] ||
          !calendarValues[1] ||
          (amenity.multiDayBooking &&
            calendarValues &&
            calendarValues.length === 2 &&
            (calculateDaysBetweenDates(calendarValues) < amenity.minimumDays ||
              (amenity.maximumDays &&
                calculateDaysBetweenDates(calendarValues) > amenity.maximumDays)))
        )
      case Views.SelectTime:
        if (amenity.timeInterval >= amenity.minTime) {
          return !selectedTimeRange || typeof selectedTimeRange.startTime !== 'number'
        }
        return (
          !selectedTimeRange ||
          typeof selectedTimeRange.startTime !== 'number' ||
          !selectedTimeRange.endTime
        )
      case Views.OccupantInfo:
        const isNoteInvalid =
          advancedSettings.note &&
          (!advancedSettings.noteTitle.trim() || !advancedSettings.noteBody.trim())

        return (
          !occupantInfo ||
          (!occupantInfo.creditCardId && !creditCardToken && !isFree()) ||
          isCreationInProgress ||
          isNoteInvalid
        )
      case Views.AddCreditCard:
      case Views.UpdateCreditCard:
        return (
          !creditCardData ||
          !creditCardData.cardCvc ||
          !creditCardData.cardExpiry ||
          !creditCardData.cardNumber ||
          !creditCardData.cardOwner
        )
      default:
        return true
    }
  }

  const getNextView = () => {
    switch (activeView) {
      case Views.SelectAmenity:
        if (amenity.multiDayBooking) {
          return Views.SelectDates
        }
        return Views.SelectDate
      case Views.SelectDate:
        if (hasAvailablePlaces) {
          return Views.SelectLocation
        }
        return Views.SelectTime
      case Views.SelectLocation:
        if (amenity.multiDayBooking) {
          return Views.OccupantInfo
        }
        return Views.SelectTime
      case Views.SelectTime:
        return Views.OccupantInfo
      case Views.SelectDates:
        if (hasLocations) {
          return Views.SelectLocation
        }
        return Views.OccupantInfo
      default:
        return undefined
    }
  }
  const handleUpdateCreditCard = () => {
    if (occupantInfo) {
      if (occupantInfo.creditCardId) {
        setActiveView(Views.UpdateCreditCard)
      } else {
        setActiveView(Views.AddCreditCard)
      }
    }
  }

  const updateCreditCardDataSubmit = async () => {
    const data = { name: creditCardData.cardOwner }
    const cardElement = elements.getElement(CardCvcElement)
    const { token } = await stripe.createToken(cardElement, data)
    setCreditCardToken(token)
    if (occupantInfo.type === OccupantType.Resident) {
      await api.setClientCreditCardData(occupantInfo.clientId, token.id)
    } else {
      await api.setGuestCreditCardData(occupantInfo.guestId, token.id)
    }
    handleGoBack()
  }
  const handleSubmit = async () => {
    // Fetch time slots when moving from date selection to next step (for hourly amenities)
    if (
      (activeView === Views.SelectDate || activeView === Views.SelectDates) &&
      !amenity?.multiDayBooking
    ) {
      setLoadingTimeScopes(true)
      await fetchTimeSlots()
    }

    if (activeView === Views.OccupantInfo) {
      const [dateStart, dateEnd] = calendarValues
      dateStart.setHours(0, 0, 0, 0)
      if (dateEnd) {
        dateEnd.setHours(0, 0, 0, 0)
      }
      const bookingData = {}
      if (creditCardToken) {
        bookingData.cardToken = creditCardToken.card.id
      }
      if (occupantType === OccupantType.Guest) {
        bookingData.guestId = occupantInfo.guestId
      } else {
        bookingData.userId = occupantInfo.userId
      }
      if (amenity.multiDayBooking) {
        if (timezone.length > 0) {
          bookingData.date = fromZonedTime(dateStart, timezone).getTime()
          bookingData.endDate = fromZonedTime(dateEnd, timezone).getTime()
        } else {
          bookingData.date = new Date(dateStart).getTime()
          bookingData.endDate = new Date(dateEnd).getTime()
        }
        bookingData.price = getFinalPrice()
      } else {
        const { startTime, endTime } = selectedTimeRange
        if (timezone.length > 0) {
          bookingData.date = fromZonedTime(
            calendarValues[0].setHours(0, 0, 0, 0),
            timezone,
          ).getTime()
        } else {
          bookingData.date = new Date(calendarValues[0].setHours(0, 0, 0, 0)).getTime()
        }
        bookingData.startTime = startTime
        bookingData.endTime = (endTime || startTime) + amenity.timeInterval
      }
      if (advancedSettings.note && advancedSettings.noteTitle && advancedSettings.noteBody) {
        bookingData.noteTitle = advancedSettings.noteTitle
        bookingData.noteBody = advancedSettings.noteBody
      }
      bookingData.sendReceipt = advancedSettings.sendReceipt
      if (location) {
        bookingData.locationId = location?.id
      }
      if (advancedSettings.modifyPrice) {
        if (advancedSettings.modifyPriceType === ModifiedPriceType.CustomPrice) {
          bookingData.customPrice = advancedSettings.modifyPriceValue
        } else {
          bookingData.discount = (getTotalPrice(true) - getTotalPrice()).toFixed(2)
        }
      }
      try {
        setIsCreationInProgress(true)
        await api.addBooking(amenity.id, bookingData)
        toggleDashboardAction()
        handleCancel()
        displaySnackbar(t('reservations:reservationForClientForm.reservationCreated'))
      } catch (error) {
        let message = error.message
        if (error.response?.data?.message) {
          message = error.response.data.message
        }
        Alert.open(message)
      } finally {
        setIsCreationInProgress(false)
      }
    } else if (activeView === Views.UpdateCreditCard || activeView === Views.AddCreditCard) {
      try {
        await updateCreditCardDataSubmit()
      } catch (error) {
        if (error.response) {
          Alert.open(error.response.data.message)
        }
      }
    } else {
      setActiveView(getNextView())
    }
  }

  const handleAddNewGuest = () => {
    setActiveView(Views.AddNewGuest)
  }

  const handleGoBack = () => {
    switch (activeView) {
      case Views.SelectAmenity:
        handleCancel()
        break
      case Views.SelectDate:
      case Views.SelectDates:
        setActiveView(Views.SelectAmenity)
        break
      case Views.SelectTime:
        setSelectedTimeRange()
        if (hasAvailablePlaces) {
          setActiveView(Views.SelectLocation)
        } else {
          setActiveView(Views.SelectDate)
        }
        break
      case Views.SelectLocation:
        setLocation()
        if (amenity.multiDayBooking) {
          setActiveView(Views.SelectDates)
        } else {
          setActiveView(Views.SelectDate)
        }
        break
      case Views.OccupantInfo:
        if (amenity.multiDayBooking) {
          if (hasLocations) {
            setActiveView(Views.SelectLocation)
          } else {
            setActiveView(Views.SelectDates)
          }
        } else {
          setActiveView(Views.SelectTime)
        }
        break
      case Views.AddCreditCard:
      case Views.UpdateCreditCard:
      case Views.AddNewGuest:
        setActiveView(Views.OccupantInfo)
        break
      default:
        break
    }
  }

  const renderProperView = (typeofView) => {
    switch (typeofView) {
      case Views.AddCreditCard:
      case Views.UpdateCreditCard:
        return <CreditCardInfoForm onChange={setCreditCardData} />
      case Views.OccupantInfo:
        return (
          <div className="occupantInfo">
            <ReservationOccupantInfo
              occupantType={occupantType}
              onChangeOccupantType={setOccupantType}
              onSelectOccupant={(occupant) => {
                setCreditCardToken(null)
                setOccupantInfo(occupant)
              }}
              isFree={isFree()}
              selectedOccupant={occupantInfo}
              residents={residents}
              buildingData={buildingData}
              getClients={getClientsAction}
              userData={userData}
              guests={guests}
              creditCardToken={creditCardToken}
              onUpdateCreditCardInformation={handleUpdateCreditCard}
              advancedSettings={advancedSettings}
              originalPrice={getTotalPrice(true)}
              loadedAllClients={loadedAllClients}
              onChangeAdvancedSettings={(diff) =>
                setAdvancedSettings({ ...advancedSettings, ...diff })
              }
              onAddNewGuest={handleAddNewGuest}
              residentCreditCardData={residentCreditCardData}
            />
          </div>
        )
      case Views.SelectDate:
      case Views.SelectDates:
        return (
          <div className="calendarView">
            {amenity?.multiDayBooking && <BookingDurationLimits amenity={amenity} />}
            <AmenityReservationCalendar
              amenity={amenity}
              displayHeader={false}
              onValueChange={setCalendarValues}
              setAvailableLocationIds={setAvailableLocationIds}
              locationId={location?.id}
              hideReservationButton
              hideSummary
            />
            <ReservationCalendarSummary
              amenity={amenity}
              calendarValues={calendarValues}
              selectedTime={selectedTimeRange}
            />
          </div>
        )
      case Views.SelectTime: {
        let filteredTimeSlots = timeSlots
        if (hasAvailablePlaces) {
          filteredTimeSlots = getFilteredTimeSlotsForLocation(location?.id, timeSlots)
        }
        return (
          <div className="calendarView">
            <BookingDurationLimits amenity={amenity} />
            <ReservationTimeSelect
              amenity={amenity}
              onGoBack={handleGoBack}
              value={selectedTimeRange}
              onValueChange={setSelectedTimeRange}
              selectedDate={calendarValues[0]}
              timeSlots={filteredTimeSlots}
              loadingTimeScopes={loadingTimeScopes}
              locationId={location?.id}
            />
            <ReservationCalendarSummary
              amenity={amenity}
              calendarValues={calendarValues}
              selectedTime={selectedTimeRange}
            />
          </div>
        )
      }
      case Views.SelectAmenity: {
        let amenitiesContent = (
          <div>{t('reservations:reservationForClientForm.noAmenitiesAvailable')}</div>
        )
        const filteredAmenities = filterAmenitiesByName(amenities, amenitySearch)
        if (amenities.length && !filteredAmenities.length) {
          amenitiesContent = <div>{t('reservations:amenitySearch.noResults')}</div>
        }
        if (filteredAmenities.length) {
          amenitiesContent = filteredAmenities.map((amenityItem) => (
            <AmenityItem
              key={amenityItem.id}
              amenity={amenityItem}
              isSelected={amenity && amenityItem.id === amenity.id}
              onClick={setAmenity}
            />
          ))
        }
        return (
          <div className="selectAmenityView">
            <div className="amenities">
              {isLoadingAmenities ? <div className="loader" /> : amenitiesContent}
            </div>
          </div>
        )
      }
      case Views.SelectLocation: {
        let locationsContent
        if (loadingTimeScopes && !hasAvailablePlaces && !amenity?.multiDayBooking) {
          locationsContent = (
            <div className="loader-container">
              <div className="loader" />
            </div>
          )
        } else if (hasAvailablePlaces) {
          locationsContent = availablePlaces.map((place) => (
            <AmenityItem
              key={place.id}
              amenity={place}
              isSelected={location && place.id === location.id}
              onClick={() => setLocation(place)}
            />
          ))
        } else {
          let dateLabel = t(
            'reservations:reservationForClientForm.noSpacesAvailable.selectedDateFallback',
          )
          if (calendarValues?.[0]) {
            dateLabel = format(calendarValues[0], 'MMMM do, yyyy')
          }
          locationsContent = (
            <div className="no-places-available">
              <div className="no-places-available__icon">
                <img
                  src="/images/calendar-block.svg"
                  alt={t('reservations:reservationForClientForm.calendarImageAlt')}
                />
              </div>
              <div className="no-places-available__content">
                <h2 className="no-places-available__title">
                  {t('reservations:reservationForClientForm.noSpacesAvailable.title')}
                </h2>
                <p className="no-places-available__subtitle">
                  {t('reservations:reservationForClientForm.noSpacesAvailable.subtitle', {
                    date: dateLabel,
                  })}
                </p>
              </div>
            </div>
          )
        }
        return (
          <div className="selectAmenityView">
            <div className="amenities">{locationsContent}</div>
          </div>
        )
      }
      case Views.AddNewGuest:
        return (
          <AddReservationGuestForm
            buildingId={buildingData.id}
            onClose={(guest, token) => {
              setOccupantInfo(guest)
              setCreditCardToken(token)
              handleGoBack()
            }}
            noCreditCard={isFree()}
          />
        )
      default:
        return null
    }
  }

  const canCalculatePrice =
    !!amenity && !!calendarValues && (amenity.multiDayBooking || !!selectedTimeRange)
  let basePrice = canCalculatePrice ? getTotalPrice(true) : 0
  if (canCalculatePrice && amenity.extraFee) {
    basePrice = getTotalPrice(true) - amenity.extraFee
  }

  return (
    <ModalContainer
      onClose={handleCancel}
      headerTitle={t('reservations:reservationForClientForm.headerTitle')}
      additionalClass="reservation-for-client-form__modal-overlay"
      headerIcon={
        <Icon
          icon="receiptBillEdit"
          viewBox="0 0 24 24"
          fill="none"
          svgProps={{
            strokeWidth: '1.5px',
            stroke: 'black',
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          }}
        />
      }
    >
      <div className="reservation-for-client-form">
        <div className="reservation-for-client-form__content-wrapper">
          <div className="reservation-for-client-form__form-container">
            <Stepper steps={stepperSteps} currentStep={reservationStep} />
            {activeView === Views.AddNewGuest && (
              <div className="reservation-for-client-form__view-header-button-container">
                <button onClick={handleGoBack} className="button button-gray">
                  {t('common:actions.cancel')}
                </button>
              </div>
            )}
            <div className="reservation-for-client-form__view-header">
              {viewHeaders[activeView]}
              {activeView === Views.SelectAmenity && (
                <AmenitySearch defaultValue={amenitySearch} onSearch={setAmenitySearch} />
              )}
            </div>
            <div className="reservation-for-client-form__content-container">
              {renderProperView(activeView)}
            </div>
          </div>
          {(activeView === Views.OccupantInfo || activeView === Views.AddNewGuest) && (
            <ReservationSummary
              amenity={amenity}
              location={location}
              totalPrice={getTotalPrice()}
              basePrice={basePrice}
              noDiscountPrice={getTotalPrice(true)}
              calendarValues={calendarValues}
              selectedTime={selectedTimeRange}
              selectedOccupant={occupantInfo}
              advancedSettings={advancedSettings}
              creditCardToken={creditCardToken}
              residentCreditCardData={residentCreditCardData}
            />
          )}
        </div>
        <ReservationFormFooter
          key={activeView}
          view={activeView}
          onGoBack={handleGoBack}
          onSubmit={handleSubmit}
          disableActionButton={isSubmitButtonDisabled()}
        />
      </div>
    </ModalContainer>
  )
}

ReservationForClientForm.propTypes = {
  handleCancel: PropTypes.func,
  residents: PropTypes.array,
  buildingData: PropTypes.object,
}

export default ReservationForClientForm
